// The pure readings of lane B3's Step 5–9 screens (no router, no React), so the node suites can load them: which DC-31
// column an application stands in, the *Passed* strip, the HM review tabs, a decision's readiness ("Require reason for
// Hold and Pass." · "Pass cannot finish without disposition reason."), the payload each decision sends, the Step 8
// card's status from `screen.open_result`, and the due tier of a slate.

import type { ScreenRecordOutcomeInput } from "@/api/tools";
import type { DueTier, PipelineStage, ScreenOutcome, ScreenStatus } from "@/mc";
import { G1_LABELS, HM_REVIEW_STATUS } from "@/mc";
import { PIPELINE_RULES } from "./labels";

/** The V1 application stage → his DC-31 column. Held stays in the column it was held from (DC-31). */
export const COLUMN_OF: Readonly<Record<string, PipelineStage>> = {
  sourced: "sourced",
  qualified: "qualified",
  in_review: "inHmReview",
  screen: "recruiterScreen",
  screened: "recruiterScreen",
  hm_round: "hmInterview",
  hm_assessed: "hmInterview",
  panel: "finalInterviewLoop",
  debrief: "finalInterviewLoop",
  offer_approved: "offer",
  alternate: "offer",
  offer: "offer",
  hired: "offer",
};

/** The stages that leave the board for the *Passed* strip (a Pass, a Step 8 Pass or Withdrawn, and every close). */
export const PASSED_STAGES: ReadonlySet<string> = new Set(["not_selected", "passed_on", "withdrawn", "declined", "reneged", "dispositioned"]);

export interface StageCard {
  stage: string;
  held_from_stage?: string | null | undefined;
}

/** Where a card stands: a DC-31 column, the Passed strip, or nowhere (a stage the board does not draw). */
export function columnOf(card: StageCard): PipelineStage | "passed" | null {
  if (PASSED_STAGES.has(card.stage)) return "passed";
  if (card.stage === "held") return COLUMN_OF[card.held_from_stage ?? "in_review"] ?? "inHmReview";
  return COLUMN_OF[card.stage] ?? null;
}

/** The board: each column's cards in the order given, and the Passed strip. */
export function boardOf<C extends StageCard>(cards: readonly C[]): { columns: Record<PipelineStage, C[]>; passed: C[] } {
  const columns = Object.fromEntries(Object.values(COLUMN_OF).map((key) => [key, [] as C[]])) as Record<PipelineStage, C[]>;
  const passed: C[] = [];
  for (const card of cards) {
    const at = columnOf(card);
    if (at === "passed") passed.push(card);
    else if (at) columns[at].push(card);
  }
  return { columns, passed };
}

/** The HM review tabs: "Pending Review -> Advanced/Held/Passed". */
export type ReviewTab = "pendingReview" | "advanced" | "held" | "passed";
export const REVIEW_TABS: readonly ReviewTab[] = ["pendingReview", "advanced", "held", "passed"];
export const REVIEW_TAB_WORD: Readonly<Record<ReviewTab, string>> = HM_REVIEW_STATUS;

export function reviewTab(card: StageCard): ReviewTab {
  if (card.stage === "in_review") return "pendingReview";
  if (card.stage === "held") return "held";
  if (PASSED_STAGES.has(card.stage)) return "passed";
  return "advanced";
}

/** One decision being made: the value, the reason chosen from the list (Hold: a hold reason; Pass: a disposition
 *  reason code) and the free reason. */
export interface DecisionDraft {
  value: "advance" | "hold" | "pass" | "";
  reason: string;
  notes: string;
}

export const EMPTY_DECISION: DecisionDraft = { value: "", reason: "", notes: "" };

/** The shortest free reason the decision tools take (`screen.decide` reason_text min length). */
export const MIN_NOTES = 3;

/** Why this decision cannot be sent yet, or null when it can. Every decision keeps its free reason on the record (the
 *  decision tools refuse one without it); a Hold also needs its configured reason (where the step has a list: Step 9's
 *  `screen.decide` takes none), a Pass its disposition reason. */
export function decisionBlock(draft: DecisionDraft, holdList = true): string | null {
  if (!draft.value) return PIPELINE_RULES.retainAdvanceHoldPass;
  if (draft.value === "pass" && !draft.reason) return PIPELINE_RULES.passNeedsReason;
  if (draft.value === "hold" && holdList && !draft.reason) return G1_LABELS.requireReason;
  if (draft.notes.trim().length < MIN_NOTES) return draft.value === "advance" ? G1_LABELS.reasonStored : G1_LABELS.requireReason;
  return null;
}

/** Why Submit waits: the first pending decision that is not ready, or null when every one is. */
export function submitBlock(pending: readonly string[], drafts: Readonly<Record<string, DecisionDraft>>): string | null {
  if (pending.length === 0) return PIPELINE_RULES.retainAdvanceHoldPass;
  for (const id of pending) {
    const why = decisionBlock(drafts[id] ?? EMPTY_DECISION);
    if (why) return why;
  }
  return null;
}

/** A Pass's disposition reason code: the server's set; the choices offered are the Admin's published `reasons:pass`. */
export type PassReasonCode = NonNullable<ScreenRecordOutcomeInput["reason_code"]>;

export interface DecisionPayload {
  application_id: string;
  value: "advance" | "hold" | "pass";
  reason_text: string;
  reason_code: PassReasonCode | null;
  hold_reason: string | null;
}

/** The decision as `candidate.batch_decide` / `candidate.decide_resume` take it. */
export function decisionPayload(applicationId: string, draft: DecisionDraft): DecisionPayload {
  if (!draft.value) throw new Error("no decision");
  return {
    application_id: applicationId,
    value: draft.value,
    reason_text: draft.notes.trim(),
    reason_code: draft.value === "pass" ? (draft.reason as PassReasonCode) : null,
    hold_reason: draft.value === "hold" ? draft.reason : null,
  };
}

/** The Step 8 card's status from `screen.open_result` (requested · scheduled · completed · outcome + its word). */
export function screenStatusOf(out: { status?: string | null; outcome?: string | null }): ScreenStatus | null {
  if (out.status === "requested") return "screenRequested";
  if (out.status === "scheduled") return "scheduled";
  if (out.status === "completed") return "completed";
  if (out.status === "outcome" && out.outcome && ["advance", "hold", "pass", "withdrawn"].includes(out.outcome)) return out.outcome as ScreenOutcome;
  return null;
}

/** A card's name: the candidate's for a seat that reads names, else the tail of the token (the HRBP's projection). */
export const nameOf = (card: { token: string; display_name?: string | null }): string => card.display_name ?? card.token.slice(-6).toUpperCase();

/** The slate's due tier: overdue once late, else due. */
export function dueTierOf(slate: { late: boolean; state: string }): DueTier {
  return slate.late || slate.state === "overdue" ? "overdue" : "due";
}

/** yyyy-mm-dd of an ISO instant (the date inputs' value), or "" for none. */
export const dayOf = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : "");

/** The instant a yyyy-mm-dd date input means: noon UTC of that day, so every business zone reads the same day. */
export const instantOf = (day: string): string | null => (/^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day}T12:00:00Z` : null);

/** The requisitions the Pipeline offers (its picker and its all-requisitions board): the rows `requisition.list` gives
 * as the requisition's own summary. A row listed only through the interviewer door (the panelist projection) grants the
 * packets, never the pipeline — `candidate.list` refuses it — so it is not offered (demo smoke D9). */
export function pipelineRows<R extends { readonly id: string; readonly projection?: string | null | undefined }>(items: readonly R[]): R[] {
  return items.filter((row) => row.projection !== "panelist");
}

/** Every requisition read came back refused: the board would be a silent empty list, so the page says the Pipeline is
 * not the seat's instead (demo smoke D9). */
export function everyReadRefused(reads: readonly unknown[] | undefined): boolean {
  return !!reads && reads.length > 0 && reads.every((read) => read === null);
}
